import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumb } from "@/components/breadcrumb";
import { Icon } from "@/components/icons";
import { Modal } from "@/components/modal";
import { PaginationFooter } from "@/components/pagination-footer";
import { Tabs } from "@/components/tabs";
import { usePaginatedMatches } from "@/components/use-paginated-matches";
import {
  MONITORING_RESULTS_COLUMNS_META,
  MONITORING_ADV_MEDIA_RESULTS_COLUMNS_META,
  ResultsColumnHeaders,
  ResultsTable,
  sortMatches,
  useMonitoringResultsColumnPrefs,
  useMonitoringAdvMediaColumnPrefs,
  useResultsSort,
  type ResultsColumnId,
} from "@/app/(app)/screening/_components/results-table";
import { TableColumnsMenu } from "@/components/table-columns-menu";
import { Tooltip } from "@/components/tooltip";
import { MatchDetail } from "@/app/(app)/screening/_components/match-detail";
import { DEFAULT_DECISION_STATUS, MatchDecisionPanel } from "@/components/match-decision-panel";
import { SearchSummary } from "@/components/search-summary";
import type { Match, MatchDecision, MatchDecisionStatus } from "@/app/(app)/screening/_data/types";
import {
  bulkUpdateMatchDecisions,
  listEntryResults,
  updateMatchDecision,
  type EntryResultsKind,
  type MonitoringEntry,
} from "@/src/services/monitoring_service";
import { useToast } from "@/components/action-feedback";
import { captureEvent } from "@/src/lib/posthog";
import { ExportPdfButton } from "./export-pdf-button";

type Props = { entry: MonitoringEntry };

type ResultsTab = "watchlists" | "adverseMedia";

/** Which of the two collections this entry was actually monitored against —
 *  an empty `dataSource` means "search all sanctions lists" (the backend's
 *  own default, mirrored in `screening_service.buildDataSource`), never
 *  Adverse Media only. */
function resolveEntryCollections(dataSource: string[] | undefined) {
  const tokens = dataSource ?? [];
  const hasAdvMedia = tokens.includes("ADV-MEDIA");
  const hasWatchlists = tokens.length === 0 || tokens.some((t) => t !== "ADV-MEDIA");
  return { hasWatchlists, hasAdvMedia };
}

export function MonitoringEntryMatches({ entry }: Props) {
  const [sort, setSort] = useResultsSort();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ResultsTab>("watchlists");
  const [decisionOverrides, setDecisionOverrides] = useState<Record<string, MatchDecision>>({});
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState<string>();
  const showToast = useToast();

  const { hasWatchlists, hasAdvMedia } = resolveEntryCollections(entry.dataSource);

  const makeFetchPage = useCallback(
    (kind: EntryResultsKind) => (page: number) => listEntryResults(entry.id, kind, page),
    [entry.id],
  );

  const watchlistsPrefs = useMonitoringResultsColumnPrefs();
  const advMediaPrefs = useMonitoringAdvMediaColumnPrefs();
  const [watchlistsPage, setWatchlistsPage] = useState(1);
  const [advMediaPage, setAdvMediaPage] = useState(1);

  const watchlistsResult = usePaginatedMatches(
    hasWatchlists ? makeFetchPage("watchlists") : null,
    entry.id,
    watchlistsPage,
    watchlistsPrefs.rowsPerPage,
  );
  const advMediaResult = usePaginatedMatches(
    hasAdvMedia ? makeFetchPage("adverse_media") : null,
    entry.id,
    advMediaPage,
    advMediaPrefs.rowsPerPage,
  );

  // Tabs reflect actual results, not just what was monitored — a collection
  // that was searched but came back empty doesn't get its own (empty) tab;
  // it falls through to whichever collection actually has rows.
  const showTabs = watchlistsResult.count > 0 && advMediaResult.count > 0;
  const effectiveTab: ResultsTab = showTabs
    ? activeTab
    : advMediaResult.count > 0
      ? "adverseMedia"
      : "watchlists";
  const isAdvMediaTab = effectiveTab === "adverseMedia";

  useEffect(() => setWatchlistsPage(1), [entry.id, watchlistsPrefs.rowsPerPage]);
  useEffect(() => setAdvMediaPage(1), [entry.id, advMediaPrefs.rowsPerPage]);

  // Reviewer decisions are saved per-match-id, independent of which
  // collection/page a row came from — applied as an overlay so a save
  // doesn't need to reach into the paginated hooks' internal state.
  const applyDecisions = useCallback(
    (matches: Match[]) =>
      matches.map((m) =>
        decisionOverrides[m.id] ? { ...m, decision: decisionOverrides[m.id] } : m,
      ),
    [decisionOverrides],
  );
  const watchlistsMatches = useMemo(
    () => applyDecisions(watchlistsResult.matches),
    [watchlistsResult.matches, applyDecisions],
  );
  const advMediaMatches = useMemo(
    () => applyDecisions(advMediaResult.matches),
    [advMediaResult.matches, applyDecisions],
  );
  const activeMatches = isAdvMediaTab ? advMediaMatches : watchlistsMatches;

  useEffect(() => {
    if (selectedId && activeMatches.some((m) => m.id === selectedId)) return;
    setSelectedId(activeMatches.length > 0 ? activeMatches[0].id : null);
  }, [activeMatches, selectedId]);

  const selectedIndex = selectedId ? activeMatches.findIndex((m) => m.id === selectedId) : -1;
  const selected = selectedIndex >= 0 ? activeMatches[selectedIndex] : null;

  function step(delta: number) {
    if (selectedIndex < 0) return;
    const next = selectedIndex + delta;
    if (next < 0 || next >= activeMatches.length) return;
    setSelectedId(activeMatches[next].id);
  }

  // Persist first, then apply to the table on success — a failed PATCH must not
  // leave a wrong status behind. Throwing lets MatchDetail surface the error and
  // keep the decision modal open for a retry.
  async function handleDecisionSave(matchId: string, decision: MatchDecision) {
    const res = await updateMatchDecision(entry.id, matchId, decision);
    if (!res.ok) throw new Error("decision-save-failed");
    setDecisionOverrides((prev) => ({ ...prev, [matchId]: decision }));
    showToast({ tone: "success", text: "The entity has been updated successfully." });
  }

  const identifierOnly = !entry.name && !!entry.identifier;
  const displayName = entry.name || entry.identifier || "—";

  const isLoading =
    (hasWatchlists && watchlistsResult.isLoading) || (hasAdvMedia && advMediaResult.isLoading);
  const loadError = watchlistsResult.error ?? advMediaResult.error;

  const wasLoadingViewRef = useRef(isLoading);
  useEffect(() => {
    const justSettled = wasLoadingViewRef.current && !isLoading;
    wasLoadingViewRef.current = isLoading;
    if (!justSettled || loadError) return;
    captureEvent("Viewed Monitoring Entry Results", {
      match_count: watchlistsResult.count + advMediaResult.count,
      watchlists_count: watchlistsResult.count,
      adverse_media_count: advMediaResult.count,
    });
  }, [isLoading, loadError, watchlistsResult.count, advMediaResult.count]);

  const activePrefs = isAdvMediaTab ? advMediaPrefs : watchlistsPrefs;
  const activeCount = isAdvMediaTab ? advMediaResult.count : watchlistsResult.count;
  const activePage = isAdvMediaTab ? advMediaPage : watchlistsPage;
  const setActivePage = isAdvMediaTab ? setAdvMediaPage : setWatchlistsPage;
  const activePageCount = Math.max(1, Math.ceil(activeCount / activePrefs.rowsPerPage));
  const safeActivePage = Math.min(activePage, activePageCount);

  const individuals = watchlistsMatches.filter((m) => m.type === "Individual").length;
  const organizations = watchlistsMatches.filter((m) => m.type === "Organization").length;
  const others = watchlistsMatches.filter(
    (m) => m.type === "Vessel" || m.type === "Aircraft" || m.type === "Country",
  ).length;

  const visibleMatches = useMemo(
    () =>
      sortMatches(activeMatches, sort.key, sort.dir).slice(
        (safeActivePage - 1) * activePrefs.rowsPerPage,
        safeActivePage * activePrefs.rowsPerPage,
      ),
    [activeMatches, sort, safeActivePage, activePrefs.rowsPerPage],
  );
  const visibleSelectedIds = visibleMatches
    .filter((m) => selectedIds.has(m.id))
    .map((m) => m.id);
  const allSelected = visibleMatches.length > 0 && visibleMatches.every((m) => selectedIds.has(m.id));
  const someSelected = visibleMatches.some((m) => selectedIds.has(m.id));

  function toggleSelect(id: string) {
    if (!visibleMatches.some((m) => m.id === id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const everySelected = visibleMatches.every((m) => prev.has(m.id));
      visibleMatches.forEach((m) => (everySelected ? next.delete(m.id) : next.add(m.id)));
      return next;
    });
  }

  async function handleBulkStatusSave(status: MatchDecisionStatus, text: string) {
    const ids = visibleSelectedIds;
    if (ids.length === 0) return;
    setBulkSaving(true);
    setBulkError(undefined);
    const res = await bulkUpdateMatchDecisions(ids, status, text);
    setBulkSaving(false);
    if (!res.ok) {
      setBulkError(`Couldn't update the selected ${ids.length === 1 ? "result" : "results"}. Try again.`);
      return;
    }
    setDecisionOverrides((prev) => {
      const next = { ...prev };
      for (const updated of res.value) {
        if (updated.decision) next[updated.id] = updated.decision;
      }
      return next;
    });
    captureEvent("Bulk update monitoring result status", { count: ids.length, status });
    setBulkModalOpen(false);
    setSelectedIds(new Set());
    setSelectMode(false);
    showToast({
      tone: "success",
      text: `${ids.length} ${ids.length === 1 ? "result has" : "results have"} been updated.`,
    });
  }

  return (
    <div className="flex min-h-screen flex-1 flex-col bg-white px-4 py-4 sm:px-8">
      <div className="flex flex-col gap-2">
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Breadcrumb
            backHref="/monitoring-portal"
            segments={[
              { label: "Monitoring", href: "/monitoring-portal" },
              { label: displayName, current: true },
            ]}
          />
          <ExportPdfButton key={entry.id} entryId={entry.id} disabled={isLoading} />
        </div>

        <SearchSummary
          name={displayName}
          matchCount={watchlistsResult.count + advMediaResult.count}
          entityType={entry.type}
          country={entry.countries ?? entry.country}
          dob={entry.dob}
          identifier={identifierOnly ? undefined : entry.identifier}
          minScore={entry.minScore}
          dataSource={entry.dataSource}
          monitoringSince={entry.dateAdded}
          actions={
            !showTabs && !isLoading && activeMatches.length > 0 ? (
              <div className="flex items-center gap-2">
                <SelectionControls
                  selectMode={selectMode}
                  onToggleSelectMode={() =>
                    setSelectMode((v) => {
                      if (v) setSelectedIds(new Set());
                      return !v;
                    })
                  }
                  selectedCount={visibleSelectedIds.length}
                  onUpdateStatus={() => {
                    setBulkError(undefined);
                    setBulkModalOpen(true);
                  }}
                />
                <TableColumnsMenu
                  columns={
                    isAdvMediaTab
                      ? MONITORING_ADV_MEDIA_RESULTS_COLUMNS_META
                      : MONITORING_RESULTS_COLUMNS_META
                  }
                  hidden={activePrefs.hidden}
                  onToggle={(id) => activePrefs.toggleHidden(id as ResultsColumnId)}
                  onReset={activePrefs.reset}
                />
              </div>
            ) : null
          }
        />

        {showTabs && (
          <div className="flex items-center justify-between gap-2">
            <Tabs
              value={activeTab}
              onChange={(id) => setActiveTab(id as ResultsTab)}
              tabs={[
                { id: "watchlists", label: `Watchlists (${watchlistsResult.count})` },
                { id: "adverseMedia", label: `Adverse Media (${advMediaResult.count})` },
              ]}
            />
            {!isLoading && activeMatches.length > 0 && (
              <div className="flex items-center gap-2">
                <SelectionControls
                  selectMode={selectMode}
                  onToggleSelectMode={() =>
                    setSelectMode((v) => {
                      if (v) setSelectedIds(new Set());
                      return !v;
                    })
                  }
                  selectedCount={visibleSelectedIds.length}
                  onUpdateStatus={() => {
                    setBulkError(undefined);
                    setBulkModalOpen(true);
                  }}
                />
                <TableColumnsMenu
                  columns={
                    isAdvMediaTab
                      ? MONITORING_ADV_MEDIA_RESULTS_COLUMNS_META
                      : MONITORING_RESULTS_COLUMNS_META
                  }
                  hidden={activePrefs.hidden}
                  onToggle={(id) => activePrefs.toggleHidden(id as ResultsColumnId)}
                  onReset={activePrefs.reset}
                />
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col overflow-hidden rounded-sm border border-grey-200 bg-white">
            <EntrySkeletonRows />
          </div>
        ) : activeMatches.length > 0 ? (
          // One scroller for both axes: the wide results table scrolls
          // horizontally as a single unit (header + rows share one min-width
          // wrapper, so columns never drift and there's no double scrollbar),
          // while the column header stays pinned on vertical scroll.
          <div className="overflow-hidden rounded-sm border border-grey-200 bg-white shadow-flat">
            <div className="max-h-90 overflow-auto">
              <div className="min-w-265">
                <div className="sticky top-0 z-10">
                  <ResultsColumnHeaders
                    sort={sort}
                    onSort={setSort}
                    order={activePrefs.order}
                    hidden={activePrefs.hidden}
                    onReorder={activePrefs.setOrder}
                    widths={activePrefs.widths}
                    onResizeColumn={activePrefs.setWidth}
                    selectMode={selectMode}
                    allSelected={allSelected}
                    someSelected={someSelected}
                    onToggleAll={toggleSelectAll}
                  />
                </div>
                <ResultsTable
                  matches={activeMatches}
                  sort={sort}
                  order={activePrefs.order}
                  hidden={activePrefs.hidden}
                  onSelect={setSelectedId}
                  selectedId={selectedId}
                  widths={activePrefs.widths}
                  page={safeActivePage}
                  rowsPerPage={activePrefs.rowsPerPage}
                  selectMode={selectMode}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                />
              </div>
            </div>
            <PaginationFooter
              total={activeCount}
              totalLabel={isAdvMediaTab ? "articles" : "matches"}
              {...(isAdvMediaTab ? {} : { individuals, organizations, others })}
              page={safeActivePage}
              pageCount={activePageCount}
              onPageChange={setActivePage}
              rowsPerPage={activePrefs.rowsPerPage}
              onRowsPerPageChange={(n) => {
                activePrefs.setRowsPerPage(n);
                setActivePage(1);
              }}
            />
          </div>
        ) : (
          <div className="rounded-sm border border-grey-200 bg-white shadow-flat">
            <div className="flex flex-col items-center gap-1.5 px-4 py-12">
              <h3 className="text-h4 text-black">No matches</h3>
              <p className="text-small text-grey-900">This name hasn’t matched anything yet.</p>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <div className="mt-3">
          <MatchDetail
            embedded
            enableDecisions
            showEntityMeta
            showAliases
            match={selected}
            position={{ current: selectedIndex + 1, total: activeMatches.length }}
            onPrev={selectedIndex > 0 ? () => step(-1) : undefined}
            onNext={
              selectedIndex >= 0 && selectedIndex < activeMatches.length - 1
                ? () => step(1)
                : undefined
            }
            onDecisionSave={(d) => handleDecisionSave(selected.id, d)}
          />
        </div>
      )}

      <Modal
        open={bulkModalOpen}
        onClose={() => {
          if (!bulkSaving) setBulkModalOpen(false);
        }}
        title="Update status"
        description={`Set ${visibleSelectedIds.length} selected ${
          visibleSelectedIds.length === 1 ? "result" : "results"
        } to a new status and add an optional comment.`}
        width={640}
        padding={24}
      >
        <MatchDecisionPanel
          key={String(bulkModalOpen)}
          status={DEFAULT_DECISION_STATUS}
          count={visibleSelectedIds.length}
          saving={bulkSaving}
          error={bulkError}
          onSave={(d) => handleBulkStatusSave(d.status, d.comment)}
          onCancel={() => setBulkModalOpen(false)}
        />
      </Modal>
    </div>
  );
}

function SelectionControls({
  selectMode,
  onToggleSelectMode,
  selectedCount,
  onUpdateStatus,
}: {
  selectMode: boolean;
  onToggleSelectMode: () => void;
  selectedCount: number;
  onUpdateStatus: () => void;
}) {
  const updateStatusButton = (
    <button
      type="button"
      onClick={onUpdateStatus}
      disabled={selectedCount === 0}
      aria-label={
        selectedCount > 0
          ? `Update status for ${selectedCount} selected ${selectedCount === 1 ? "result" : "results"}`
          : "Update status for selected results"
      }
      className={[
        "flex items-center gap-1 rounded-[4px] border border-grey-200 bg-white p-2 text-small font-medium shadow-flat",
        selectedCount > 0 ? "text-black hover:bg-grey-50" : "cursor-not-allowed text-grey-300",
      ].join(" ")}
    >
      <Icon name="edit_square" size={20} weight={300} />
      <span>{selectedCount > 0 ? `Update status (${selectedCount})` : "Update status"}</span>
    </button>
  );

  return (
    <div className="flex items-center gap-1">
      {selectMode &&
        (selectedCount === 0 ? (
          <Tooltip title="Select a result to update its status" align="end">
            {updateStatusButton}
          </Tooltip>
        ) : (
          updateStatusButton
        ))}
      <button
        type="button"
        onClick={onToggleSelectMode}
        aria-pressed={selectMode}
        className={[
          "flex items-center gap-1 rounded-[4px] border p-2 text-small font-medium shadow-flat",
          selectMode
            ? "border-grey-500 bg-grey-100 text-black"
            : "border-grey-200 bg-white text-black hover:bg-grey-50",
        ].join(" ")}
      >
        <Icon name="check_box" size={20} weight={300} />
        <span>Select</span>
      </button>
    </div>
  );
}

function EntrySkeletonRows() {
  return (
    <div role="status" aria-label="Loading matches" className="flex flex-col">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex h-12 animate-pulse items-center gap-3 border-b border-grey-100 px-3"
          style={{ animationDelay: `${i * 80}ms` }}
        >
          <div className="h-4 flex-1 max-w-65 rounded-sm bg-grey-200" />
          <div className="h-4 w-32 rounded-sm bg-grey-200" />
          <div className="h-4 w-20 rounded-sm bg-grey-200" />
          <div className="h-4 w-24 rounded-sm bg-grey-200" />
        </div>
      ))}
    </div>
  );
}
